// analytics.js — PostHog wrapper
//
// Single place where we touch the PostHog SDK so the __loaded guard lives
// once. If REACT_APP_POSTHOG_KEY is missing, init is skipped and every
// helper becomes a silent no-op.
//
// BL-025 Phase 2 — analytics environment isolation. A SINGLE shared PostHog
// project backs every tier (no second project — PO decision, June 4 2026).
// Production analytics stay trustworthy because:
//   1. init registers the canonical `environment` as a PostHog super-property,
//   2. every track()/identify() call injects `environment` into the payload
//      (so events are tagged and the identified profile carries it),
//   3. the analytics safety guard (`assertEnvironmentTag`) forbids emitting any
//      event without a valid environment value — fail-fast outside production
//      builds (so the regression is caught in dev / CI / preview), fail-closed
//      inside a production build (log + refuse to emit rather than throw in the
//      user-facing app, or pollute prod dashboards with an unclassified event).

import posthog from 'posthog-js';
import { getEnvironment, isValidEnvironment, ENVIRONMENTS } from './environment';

const KEY  = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

let initialized = false;

// Analytics safety guard (sibling in spirit to the Supabase ref-guard).
// Returns the value when it is a valid canonical environment; otherwise it
// fails fast outside a production build (throw) and fails closed inside one
// (console.error + return null, so the caller emits nothing).
export function assertEnvironmentTag(env) {
  if (isValidEnvironment(env)) return env;
  const message =
    `[analytics] BL-025 guard: refused to emit an analytics event without a ` +
    `valid environment tag (got ${JSON.stringify(env)}; expected one of: ` +
    `${Object.values(ENVIRONMENTS).join(', ')}).`;
  if (process.env.NODE_ENV !== 'production') {
    throw new Error(message);
  }
  if (typeof console !== 'undefined' && console.error) console.error(message);
  return null;
}

export function initAnalytics() {
  if (initialized || !KEY) return;
  posthog.init(KEY, {
    api_host: HOST,
    autocapture: false,       // manual events only
    persistence: 'localStorage',
    capture_pageview: false,
  });
  initialized = true;
  // Tag every subsequent event with the environment via a super-property —
  // belt-and-suspenders alongside the per-call injection below.
  const environment = getEnvironment();
  if (isValidEnvironment(environment)) {
    posthog.register({ environment });
  }
}

function ready() {
  return initialized && posthog.__loaded;
}

export function identify(userId, traits = {}) {
  if (!ready() || !userId) return;
  const environment = assertEnvironmentTag(getEnvironment());
  if (!environment) return;
  posthog.identify(userId, { ...traits, environment });
}

export function reset() {
  if (!ready()) return;
  posthog.reset();
}

export function track(event, properties = {}) {
  if (!ready()) return;
  const environment = assertEnvironmentTag(getEnvironment());
  if (!environment) return;
  posthog.capture(event, { ...properties, environment });
}
